import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState, type ReactElement } from "react";
import { useToast } from "@cx/ui";
import type { AnalyticsStatus, BiExportReport } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

/** Instante ISO → "dd/mm/aaaa hh:mm" no horário de Brasília. */
const SP = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
export const dateTimeBR = (iso: string) => SP.format(new Date(iso)).replace(",", "");

/** Situação do lago (último export, pendentes, canais e situações). */
export function useAnalyticsStatus() {
  const { companyId, api } = useSession();
  return useQuery({
    queryKey: ["analytics", companyId, "status"],
    queryFn: () => api<AnalyticsStatus>("/analytics/status"),
    staleTime: 30_000,
  });
}

/**
 * Selo "Dados até …" das telas do histórico e o botão Atualizar agora (dashboard:update): enfileira o export e
 * consulta a situação a cada 2 s (até 1 min) até o último export mudar; aí recarrega as telas.
 */
export function HistoryStatus(): ReactElement {
  const { companyId, api, me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const status = useAnalyticsStatus();
  const [waitingSince, setWaitingSince] = useState<{ at: string | null; t: number } | null>(null);
  const canUpdate = can(me.permissions as Permissions, "dashboard", "update");
  const run = useMutation({
    mutationFn: () => api("/analytics/export", { method: "POST" }),
    onSuccess: () => {
      toast("info", "Atualização do histórico pedida. Os dados aparecem em instantes.");
      setWaitingSince({ at: status.data?.lastExportAt ?? null, t: Date.now() });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  useEffect(() => {
    if (!waitingSince) return;
    const h = setInterval(() => {
      void status.refetch().then((r) => {
        const changed = r.data && r.data.lastExportAt !== waitingSince.at && r.data.pendingOrders === 0;
        if (changed || Date.now() - waitingSince.t > 60_000) {
          setWaitingSince(null);
          void qc.invalidateQueries({ queryKey: ["analytics", companyId] });
        }
      });
    }, 2_000);
    return () => clearInterval(h);
  }, [waitingSince, status, qc, companyId]);
  const s = status.data;
  return (
    <div className="d-flex flex-wrap align-items-center gap-2" data-testid="history-status">
      <span className="small text-muted">
        {s?.lastExportAt
          ? `Dados até ${dateTimeBR(s.lastExportAt)}`
          : s
            ? "Histórico ainda não gerado"
            : "Carregando…"}
        {s && s.pendingOrders > 0 && ` · ${s.pendingOrders.toLocaleString("pt-BR")} pedido(s) a enviar`}
      </span>
      {canUpdate && (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={run.isPending || waitingSince !== null}
          onClick={() => run.mutate()}
        >
          {waitingSince ? "Atualizando…" : "Atualizar agora"}
        </button>
      )}
    </div>
  );
}

/** Filtro de várias opções num painel recolhível (canais). Sem escolha (undefined) = todos. */
export function MultiFilter({
  label,
  options,
  value,
  onChange,
  allLabel,
}: {
  label: string;
  options: { key: string; label: string }[];
  value: string[] | undefined;
  onChange(v: string[] | undefined): void;
  allLabel: string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const panel = useId();
  const chosen = value ?? options.map((o) => o.key);
  const toggle = (k: string) => {
    const next = chosen.includes(k) ? chosen.filter((x) => x !== k) : [...chosen, k];
    if (next.length) onChange(next.length === options.length ? undefined : next);
  };
  const summary =
    value === undefined
      ? allLabel
      : value.length === 1
        ? (options.find((o) => o.key === value[0])?.label ?? "1 selecionado")
        : `${value.length} selecionados`;
  return (
    <div className="position-relative">
      <span className="form-label small d-block mb-2">{label}</span>
      <button
        type="button"
        className="btn btn-outline-secondary w-100 text-start text-truncate"
        aria-expanded={open}
        aria-controls={panel}
        onClick={() => setOpen(!open)}
      >
        {summary}
      </button>
      {open && (
        <div
          id={panel}
          className="card shadow position-absolute z-3 mt-1 p-2"
          style={{ minWidth: "16rem", maxHeight: "20rem", overflowY: "auto" }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
        >
          <fieldset>
            <legend className="visually-hidden">{label}</legend>
            {options.map((o) => (
              <div className="form-check" key={o.key}>
                <input
                  id={`${panel}-${o.key}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={chosen.includes(o.key)}
                  onChange={() => toggle(o.key)}
                />
                <label className="form-check-label" htmlFor={`${panel}-${o.key}`}>
                  {o.label}
                </label>
              </div>
            ))}
          </fieldset>
          <div className="d-flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-sm btn-link px-0"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              Todos
            </button>
            <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={() => setOpen(false)}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Exportar CSV/Excel pela fila (mecanismo da T3.6): só com custom_reports:export; o arquivo sai em Ações assíncronas.
 * `params` = os mesmos parâmetros da tela.
 */
export function ExportButtons({
  report,
  params,
}: {
  report: BiExportReport;
  params: Record<string, string | undefined>;
}): ReactElement | null {
  const { companyId, api, me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const exp = useMutation({
    mutationFn: (fileFormat: "csv" | "xlsx") =>
      api<{ jobId: string }>("/analytics/exports", {
        method: "POST",
        body: JSON.stringify({
          report,
          fileFormat,
          params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== "")),
        }),
      }),
    onSuccess: (_r, f) => {
      toast(
        "info",
        `Exportação em ${f === "csv" ? "CSV" : "Excel"} na fila. Baixe em Ações assíncronas, no topo.`,
      );
      void qc.invalidateQueries({ queryKey: ["jobs", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  if (!can(me.permissions as Permissions, "custom_reports", "export")) return null;
  return (
    <div className="btn-group btn-group-sm" role="group" aria-label="Exportar">
      <button
        type="button"
        className="btn btn-outline-secondary"
        disabled={exp.isPending}
        onClick={() => exp.mutate("csv")}
      >
        Exportar CSV
      </button>
      <button
        type="button"
        className="btn btn-outline-secondary"
        disabled={exp.isPending}
        onClick={() => exp.mutate("xlsx")}
      >
        Exportar Excel
      </button>
    </div>
  );
}

/** Campo de data do filtro (par De/Até). */
export function DateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string | undefined;
  onChange(v: string | undefined): void;
}): ReactElement {
  return (
    <div className="col-6 col-md-2">
      <label className="form-label small" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="date"
        className="form-control"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    </div>
  );
}

/** Lista da URL ("a,b") → array; vazio = undefined. */
export const listOf = (s: string | undefined) => (s ? s.split(",").filter(Boolean) : undefined);
